import { z } from 'zod';

// ---------------------------------------------------------------------------
// Feedback Pool schemas.
//
// Mirrors public.feedback_pool as it stands after migration 0016. Source
// is now the discriminator across ingestion paths; Discord watched-channel
// messages land as `discord_channel`, with thread / reply linkage carried
// as source-side external ids (see migration header for why we don't
// resolve to uuid FKs at write time).
// ---------------------------------------------------------------------------

export const FeedbackSourceSchema = z.enum([
  'discord_slash',
  'discord_dm',
  'discord_channel',
  'sdk_in_game',
  'portal_manual',
  // 'form' lands with migration 0028 alongside the Feedback Forms epic.
  // form_id and form_submission_id columns on feedback_pool carry the
  // attribution; see packages/types/src/schemas/feedback-forms.ts.
  'form',
  // 'playtest' lands with migration 0050 alongside the Playtests epic.
  // playtest_session_id / playtest_event_id / playtest_question_id
  // columns on feedback_pool carry the attribution; see
  // packages/types/src/schemas/playtest.ts.
  'playtest',
  // 'poll' lands with migration 0091. Synthesised by the bot poll
  // finaliser on close: one row per poll with raw_text serialising
  // the question + ranked tally, and the new poll_id column carrying
  // the FK to feedback_poll.
  'poll',
]);
export type FeedbackSource = z.infer<typeof FeedbackSourceSchema>;

// Layer 0 deterministic enrichment outputs (see AI Strategy doc). Stored
// inline on the feedback record so prompt-slice queries never need a join
// to read them. Keep the shape stable and additive - the normaliser writes
// it; downstream code reads it. New flags should land here before they
// appear in any AI prompt template.
export const SignalFlagsSchema = z
  .object({
    isEmpty: z.boolean(),
    isLinkOnly: z.boolean(),
    isGifOnly: z.boolean(),
    isEmojiOnly: z.boolean(),
    hasMention: z.boolean(),
    hasReply: z.boolean(),
    hasPoll: z.boolean(),
    attachmentCounts: z.object({
      image: z.number().int().nonnegative(),
      video: z.number().int().nonnegative(),
      audio: z.number().int().nonnegative(),
      other: z.number().int().nonnegative(),
    }),
    messageType: z.number().int(),
  })
  .partial()
  .passthrough();
export type SignalFlags = z.infer<typeof SignalFlagsSchema>;

// Denormalised attachment summary kept on the feedback row. The full
// Discord attachment payload lives in raw_discord_event; this is what the
// portal renders and what AI prompts mention as "[image attached]".
export const FeedbackAttachmentSchema = z.object({
  kind: z.enum(['image', 'video', 'audio', 'file']),
  name: z.string().nullable(),
  size: z.number().int().nonnegative(),
  // Bare URL with the CDN signature stripped. The original is in the
  // archive if we ever need to refetch.
  url: z.string(),
});
export type FeedbackAttachment = z.infer<typeof FeedbackAttachmentSchema>;

// Client-facing camelCase shape. Used by the portal and any other Node-side
// consumer. Snake-cased row variant + transform follows below for the
// supabase-js read path.
export const FeedbackRecordSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
  buildMarkerId: z.string().uuid().nullable(),
  playerTokenId: z.string().uuid().nullable(),
  source: FeedbackSourceSchema,
  // Source-side natural id (Discord messageId etc.). Null for portal_manual.
  externalId: z.string().nullable(),
  discordConnectionId: z.string().uuid().nullable(),
  authorExternalId: z.string().nullable(),
  authorHandle: z.string().nullable(),
  authorIsBot: z.boolean(),
  rawText: z.string(),
  language: z.string().nullable(),
  postedAt: z.string().datetime({ offset: true }).nullable(),
  editedAt: z.string().datetime({ offset: true }).nullable(),
  capturedAt: z.string().datetime({ offset: true }),
  replyToExternalId: z.string().nullable(),
  threadId: z.string().nullable(),
  // Channel attribution snapshotted at ingest (migration 0017). For threads
  // discordChannelId equals threadId; discordParentChannelName carries the
  // forum's name so the inbox can render "thread X in #forum".
  discordChannelId: z.string().nullable(),
  discordChannelName: z.string().nullable(),
  discordParentChannelId: z.string().nullable(),
  discordParentChannelName: z.string().nullable(),
  signalFlags: SignalFlagsSchema,
  attachments: z.array(FeedbackAttachmentSchema),
  rawEventId: z.string().uuid().nullable(),
  discordMessageType: z.number().int().nullable(),
  // Form attribution lands in 0028 (source = 'form'). Both columns are
  // null for every other source.
  formId: z.string().uuid().nullable(),
  formSubmissionId: z.string().uuid().nullable(),
  // Playtest attribution lands in 0050 (source = 'playtest'). All three
  // columns are null for every other source.
  playtestSessionId: z.string().uuid().nullable().optional(),
  playtestEventId: z.string().uuid().nullable().optional(),
  playtestQuestionId: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime({ offset: true }),
});
export type FeedbackRecord = z.infer<typeof FeedbackRecordSchema>;

// Insert shape: what the bot hands to the inserter. The DB fills id,
// captured_at, created_at, so they're optional here. embedding is excluded
// because it's written by a downstream Layer 0 step, not by the normaliser.
export const FeedbackRecordInsertSchema = z.object({
  studio_id: z.string().uuid(),
  game_id: z.string().uuid(),
  build_marker_id: z.string().uuid().nullable().optional(),
  player_token_id: z.string().uuid().nullable().optional(),
  source: FeedbackSourceSchema,
  external_id: z.string().nullable().optional(),
  discord_connection_id: z.string().uuid().nullable().optional(),
  author_external_id: z.string().nullable().optional(),
  author_handle: z.string().nullable().optional(),
  author_is_bot: z.boolean().default(false),
  raw_text: z.string(),
  language: z.string().nullable().optional(),
  posted_at: z.string().datetime({ offset: true }).nullable().optional(),
  edited_at: z.string().datetime({ offset: true }).nullable().optional(),
  reply_to_external_id: z.string().nullable().optional(),
  thread_id: z.string().nullable().optional(),
  discord_channel_id: z.string().nullable().optional(),
  discord_channel_name: z.string().nullable().optional(),
  discord_parent_channel_id: z.string().nullable().optional(),
  discord_parent_channel_name: z.string().nullable().optional(),
  signal_flags: SignalFlagsSchema.default({}),
  attachments: z.array(FeedbackAttachmentSchema).default([]),
  raw_event_id: z.string().uuid().nullable().optional(),
  discord_message_type: z.number().int().nullable().optional(),
  form_id: z.string().uuid().nullable().optional(),
  form_submission_id: z.string().uuid().nullable().optional(),
  // For discord_channel rows that are a reply / thread message on a form
  // submission's summary embed. Lets the Messages feed filter them out
  // since the form-submissions feed already renders them under the parent.
  form_engagement_submission_id: z.string().uuid().nullable().optional(),
  // Playtest attribution, set when source = 'playtest' (PR 4 inserts).
  playtest_session_id: z.string().uuid().nullable().optional(),
  playtest_event_id: z.string().uuid().nullable().optional(),
  playtest_question_id: z.string().uuid().nullable().optional(),
});
export type FeedbackRecordInsert = z.infer<typeof FeedbackRecordInsertSchema>;

// Sibling row inserts used by the Discord normaliser.

export const FeedbackPollInsertSchema = z.object({
  question: z.string(),
  allow_multiselect: z.boolean().default(false),
  layout_type: z.number().int().default(1),
  expires_at: z.string().datetime({ offset: true }).nullable().optional(),
  finalised: z.boolean().default(false),
  answers: z.array(
    z.object({
      id: z.number().int(),
      text: z.string().nullable(),
      emoji: z
        .object({
          id: z.string().nullable(),
          name: z.string().nullable(),
          animated: z.boolean().nullable(),
        })
        .nullable(),
      voteCount: z.number().int().nonnegative(),
    }),
  ),
});
export type FeedbackPollInsert = z.infer<typeof FeedbackPollInsertSchema>;

export const FeedbackReactionInsertSchema = z.object({
  reactor_external_id: z.string(),
  emoji: z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    animated: z.boolean().nullable(),
    identifier: z.string(),
  }),
  action: z.enum(['add', 'remove']),
});
export type FeedbackReactionInsert = z.infer<typeof FeedbackReactionInsertSchema>;
